'use client';

import { useState, useMemo } from 'react';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import LivePriceIndicator from '@/components/common/LivePriceIndicator';
import AffiliateDisclosure from '@/components/common/AffiliateDisclosure';
import type { HotelSearchParams } from './HotelSearchInteractive';
import { trackEvent } from '@/lib/analytics';

interface HotelResultsProps {
  searchParams: HotelSearchParams;
}

const HotelResults = ({ searchParams }: HotelResultsProps) => {
  const [sortBy, setSortBy] = useState('recommended');

  // Mock hotel data - In production, this would come from API
  const hotels = useMemo(() => [
  {
    id: '1',
    name: 'Grand Plaza Hotel',
    location: 'Downtown, New York',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d670e8fe-1772126504855.png",
    alt: 'Luxury hotel exterior with modern architecture and glass facade',
    rating: 4.8,
    reviewCount: 1247,
    stars: 5,
    pricePerNight: 299,
    originalPrice: 399,
    amenities: ['Free WiFi', 'Pool', 'Gym', 'Restaurant', 'Spa'],
    distance: '0.5 km from center',
    cancellation: 'Free cancellation'
  },
  {
    id: '2',
    name: 'Seaside Resort & Spa',
    location: 'Beachfront, Miami',
    image: "https://images.unsplash.com/photo-1610072878098-fec8558e1561",
    alt: 'Beachfront resort with palm trees and ocean view',
    rating: 4.9,
    reviewCount: 892,
    stars: 5,
    pricePerNight: 449,
    originalPrice: 599,
    amenities: ['Beach Access', 'Pool', 'Spa', 'Restaurant', 'Bar'],
    distance: 'Beachfront',
    cancellation: 'Free cancellation'
  },
  {
    id: '3',
    name: 'City Center Inn',
    location: 'Financial District, San Francisco',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_161621856-1772297241776.png",
    alt: 'Modern boutique hotel in city center with contemporary design',
    rating: 4.6,
    reviewCount: 634,
    stars: 4,
    pricePerNight: 189,
    originalPrice: 249,
    amenities: ['Free WiFi', 'Breakfast', 'Gym', 'Business Center'],
    distance: '0.3 km from center',
    cancellation: 'Free cancellation'
  },
  {
    id: '4',
    name: 'Mountain View Lodge',
    location: 'Aspen, Colorado',
    image: "https://images.unsplash.com/photo-1581700904047-27941ecae272",
    alt: 'Mountain lodge with wooden architecture surrounded by pine trees',
    rating: 4.7,
    reviewCount: 421,
    stars: 4,
    pricePerNight: 329,
    originalPrice: null,
    amenities: ['Ski Access', 'Fireplace', 'Restaurant', 'Hot Tub'],
    distance: '2 km from slopes',
    cancellation: 'Free cancellation'
  },
  {
    id: '5',
    name: 'Historic Boutique Hotel',
    location: 'Old Town, Prague',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b636f8f-1772297239099.png",
    alt: 'Historic boutique hotel with classic European architecture',
    rating: 4.9,
    reviewCount: 1089,
    stars: 5,
    pricePerNight: 259,
    originalPrice: 329,
    amenities: ['Free WiFi', 'Breakfast', 'Bar', 'Concierge'],
    distance: '0.1 km from center',
    cancellation: 'Free cancellation'
  },
  {
    id: '6',
    name: 'Airport Express Hotel',
    location: 'Near Airport, London',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11857b785-1772297241903.png",
    alt: 'Modern airport hotel with sleek design and comfortable rooms',
    rating: 4.4,
    reviewCount: 756,
    stars: 3,
    pricePerNight: 129,
    originalPrice: null,
    amenities: ['Free WiFi', 'Airport Shuttle', 'Parking', '24h Reception'],
    distance: '1 km from airport',
    cancellation: 'Free cancellation'
  }], []);

  const handleBookingClick = (hotelId: string, pricePerNight: number) => {
    trackEvent('hotel_lead_request', {
      hotel_id: hotelId,
      price: pricePerNight
    });

    // Redirect to booking interest form
    const params = new URLSearchParams({
      type: 'hotel',
      destination: searchParams.destination || '',
      date: searchParams.checkIn || '',
      price: (pricePerNight * (searchParams.rooms || 1)).toString()
    });
    window.location.href = `/booking-interest?${params.toString()}`;
  };

  const handleDirectBooking = (hotelId: string, hotelName: string, pricePerNight: number) => {
    trackEvent('hotel_direct_booking', {
      hotel_id: hotelId,
      hotel_name: hotelName,
      price: pricePerNight
    });

    // Redirect to booking page with hotel details
    const params = new URLSearchParams({
      type: 'hotel',
      hotelId: hotelId,
      hotelName: hotelName,
      destination: searchParams.destination || '',
      checkIn: searchParams.checkIn || '',
      checkOut: searchParams.checkOut || '',
      rooms: (searchParams.rooms || 1).toString(),
      price: pricePerNight.toString()
    });
    window.location.href = `/booking?${params.toString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card rounded-lg shadow-brand p-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground">
            {hotels.length} Hotels Found
          </h2>
          <p className="text-sm text-muted-foreground">
            {searchParams.destination && `in ${searchParams.destination}`}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground font-bold">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border-2 border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-bold text-foreground">
            
            <option value="recommended">Recommended</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="distance">Distance</option>
          </select>
        </div>
      </div>

      {/* Affiliate Disclosure */}
      <AffiliateDisclosure partner="Booking.com" />

      {/* Hotel Cards */}
      <div className="space-y-4">
        {hotels.map((hotel) =>
        <div
          key={hotel.id}
          className="bg-card rounded-lg shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group">
          
            <div className="flex flex-col md:flex-row">
              {/* Hotel Image */}
              <div className="relative md:w-80 h-64 md:h-auto flex-shrink-0 overflow-hidden">
                <AppImage
                src={hotel.image}
                alt={hotel.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
                {hotel.originalPrice &&
              <div className="absolute top-4 left-4 bg-coral text-white px-3 py-1.5 rounded-full text-xs font-heading font-semibold">
                    Save ${hotel.originalPrice - hotel.pricePerNight}
                  </div>
              }
              </div>

              {/* Hotel Info */}
              <div className="flex-1 p-6">
                <div className="flex flex-col h-full">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {Array.from({ length: hotel.stars }).map((_, i) =>
                        <Icon key={i} name="StarIcon" size={16} className="text-accent" variant="solid" />
                        )}
                        </div>
                        <h3 className="font-heading font-bold text-xl text-foreground mb-1">
                          {hotel.name}
                        </h3>
                        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
                          <Icon name="MapPinIcon" size={14} />
                          <span>{hotel.location}</span>
                          <span>•</span>
                          <span>{hotel.distance}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex items-center space-x-1">
                        <div className="bg-primary text-white px-2 py-1 rounded font-bold text-sm">
                          {hotel.rating}
                        </div>
                        <div className="text-sm">
                          <span className="font-bold text-foreground">Excellent</span>
                          <span className="text-muted-foreground"> ({hotel.reviewCount} reviews)</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {hotel.amenities.slice(0, 5).map((amenity, index) =>
                    <span
                      key={index}
                      className="px-3 py-1 bg-surface text-foreground text-xs rounded-full font-bold">
                      
                          {amenity}
                        </span>
                    )}
                    </div>

                    <div className="flex items-center space-x-2 text-success text-sm font-bold">
                      <Icon name="CheckCircleIcon" size={16} />
                      <span>{hotel.cancellation}</span>
                    </div>
                  </div>

                  {/* Price and Book */}
                  <div className="flex items-center justify-between pt-4 border-t border-border/30">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total for {searchParams.rooms || 1} room(s)</p>
                      <div className="flex items-baseline gap-2">
                        {hotel.originalPrice &&
                      <span className="text-lg text-muted-foreground line-through">
                            ${hotel.originalPrice}
                          </span>
                      }
                        <span className="text-3xl font-heading font-black text-primary">
                          ${hotel.pricePerNight}
                        </span>
                        <span className="text-sm text-muted-foreground">/night</span>
                        <LivePriceIndicator
                        itemId={`hotel-${hotel.id}`}
                        basePrice={hotel.pricePerNight}
                        volatility="medium"
                        showTrend={true}
                        updateInterval={10000} />
                      
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        + £20 service fee per room
                      </p>
                    </div>
                    <button
                    onClick={() => handleBookingClick(hotel.id, hotel.pricePerNight)}
                    className="bg-gradient-to-r from-primary to-secondary text-white font-heading font-black px-8 py-4 rounded-xl hover:shadow-xl transition-all duration-300 flex items-center gap-2 group">
                    
                      <span>Request Booking</span>
                      <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    
                    <button
                    onClick={() => handleDirectBooking(hotel.id, hotel.name, hotel.pricePerNight)}
                    className="mt-2 w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2">
                    
                      <Icon name="CreditCardIcon" size={20} />
                      <span>Book & Pay Now</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>);

};

export default HotelResults;